import { Box, Button, Flex, Skeleton, Text } from "@chakra-ui/react";
import { ETH, RSETH, WRSETH } from "@config/contractsConfig";
import { CONNECT_WALLET, STABLE_VAULT } from "@constants/analytics";
import {
  APPROVE_CTA,
  CONNECT_WALLET_CTA,
  DEPOSIT_NOW,
  MAX_FRACTION_DIGITS,
  SWITCH_NETWORK,
  toFixedWithoutRounding,
} from "@constants/common";
import { EVENTS } from "@constants/reduxEvents";
import useDappConfig from "@hooks/useDappConfig";
import useExchangeRate from "@hooks/useExchangeRate";
import useStableAssetExchangeRate from "@hooks/useStableAssetExchangeRate";
import useStableVaultAllowance from "@hooks/useStableVaultAllowance";
import useStableVaultDepositLimit from "@hooks/useStableVaultDepositLimit";
import useTokenBalance from "@hooks/useTokenBalance";
import { useConnectModal } from "@rainbow-me/rainbowkit";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { updateUserData } from "store";
import {
  emitReduxEvent,
  formatNumber,
  isFloat,
  toStringPrecision,
  trackEvent,
} from "utils";
import { useAccount } from "wagmi";
import StableTicketInput from "../StableTicketInput";
import StableTicketInputBalance from "../StableTicketInputBalance";
import StableTokenSelect from "../StableTokenSelect";

const StableDeposit = () => {
  const [amount, setAmount] = useState("");
  const [error, setError] = useState("");

  const { isConnected, address } = useAccount();

  const { isStableVaultSupportedChain, config, stableVaultConfig } =
    useDappConfig();

  const { erc20Allowance, minDepositAmount } = useStableVaultAllowance();

  const {
    tokensBalances,
    xTokenTokenBalance,
    xTokenStableVaultBalance,
    nativeTokenBalance,
  } = useTokenBalance();

  const { selectedERC20StableVault, referralId, stableFormResetNonce } =
    useSelector((state: any) => state.user);
  const dispatch = useDispatch();

  const tokenBalance = useMemo(() => {
    if (selectedERC20StableVault === config.xtoken.symbol)
      return xTokenTokenBalance;
    else if (selectedERC20StableVault === ETH) return nativeTokenBalance;
    return tokensBalances[selectedERC20StableVault] || 0;
  }, [
    xTokenTokenBalance,
    tokensBalances,
    selectedERC20StableVault,
    nativeTokenBalance,
    config.xtoken.symbol,
  ]);

  const conversionDataRsEth = useExchangeRate("stablevault");
  const stableAssetExchangeRate = useStableAssetExchangeRate();

  const conversionsRsETH = useMemo(() => {
    const conversionToERC20 = conversionDataRsEth?.exchangeRate;
    return {
      toERC20: conversionToERC20 || 0,
      fromERC20: conversionToERC20 ? 1 / conversionToERC20 : 0,
    };
  }, [conversionDataRsEth?.exchangeRate]);

  const getsbUsdExchange = useCallback(() => {
    if (
      selectedERC20StableVault === "USDC" ||
      selectedERC20StableVault === "USDT"
    ) {
      return Number(stableAssetExchangeRate?.toERC20);
    }

    if (
      selectedERC20StableVault === RSETH ||
      selectedERC20StableVault === WRSETH
    ) {
      return 1;
    }

    const rsETHRate = Number(conversionsRsETH?.toERC20);
    return Number(stableAssetExchangeRate?.toERC20) * rsETHRate;
  }, [
    stableAssetExchangeRate?.toERC20,
    conversionsRsETH?.toERC20,
    selectedERC20StableVault,
  ]);

  const { depositLimit, maxDeposit } = useStableVaultDepositLimit();

  const insufficientFund = false;

  const { openConnectModal } = useConnectModal();

  const checkInput = useCallback(
    (value = amount.trim()) => {
      if (!isConnected) return "";
      let error = "";
      if (!+value || Number.isNaN(+value)) {
        error =
          "This field can't be blank or zero. Please provide valid input.";
      } else if (Number(value) > tokenBalance) {
        error = `You can't deposit more than available balance in your wallet.`;
      } else if (Number(value) < minDepositAmount) {
        error = `Stable vault has a minimum deposit of ${minDepositAmount} ${selectedERC20StableVault}.
            Please try with a higher amount.`;
      } else if (Number(value) > Number(depositLimit)) {
        error = `Stable vault allows up to ${maxDeposit} ${selectedERC20StableVault}.
            You can deposit up to ${toFixedWithoutRounding(
              Number(depositLimit).toString(),
              4
            )} ${selectedERC20StableVault}`;
      }
      return error;
    },
    [
      isConnected,
      tokenBalance,
      selectedERC20StableVault,
      amount,
      tokensBalances,
    ]
  );

  useEffect(() => {
    if (amount !== "") {
      const error = checkInput(amount);
      setError(error);
    }
  }, [tokenBalance, address, amount, checkInput]);

  const isApproved = useMemo(() => {
    if (selectedERC20StableVault === ETH) return true;
    return (
      erc20Allowance &&
      Number(erc20Allowance) &&
      Number(erc20Allowance) >= Number(amount)
    );
  }, [erc20Allowance, amount, selectedERC20StableVault]);
  useEffect(() => {
    setError("");
  }, [selectedERC20StableVault]);

  // Clear amount & error when Services reports a successful tx
  useEffect(() => {
    if (stableFormResetNonce) {
      setAmount("");
      setError("");
    }
  }, [stableFormResetNonce]);

  const convertedAmount = useMemo(() => {
    const hgRate = getsbUsdExchange();
    if (amount && !isNaN(Number(amount))) {
      const value = Number(amount) / (hgRate > 0 ? hgRate : 1);
      const formatedValue =
        value && isFloat(value)
          ? toFixedWithoutRounding(
              toStringPrecision(value),
              MAX_FRACTION_DIGITS
            )
          : toStringPrecision(value);
      if (!Number.isNaN(+formatedValue)) {
        return formatedValue;
      } else {
        return "0";
      }
    } else {
      return "0";
    }
  }, [amount, getsbUsdExchange]);

  const getButtonText = () => {
    if (!isConnected) {
      return CONNECT_WALLET_CTA;
    } else if (!isStableVaultSupportedChain) {
      return SWITCH_NETWORK;
    } else {
      if (isApproved) {
        return DEPOSIT_NOW;
      }
      return APPROVE_CTA;
    }
  };

  const handleClick = useCallback(() => {
    if (!isConnected) {
      openConnectModal?.();
      trackEvent(CONNECT_WALLET, {
        type: STABLE_VAULT.DEPOSIT_CTA,
      });
    } else if (!isStableVaultSupportedChain) {
      emitReduxEvent(EVENTS.SWITCH_NETWORK);
    } else {
      if (isApproved) {
        if (!amount) {
          setError(
            "This field can't be blank or zero. Please provide valid input."
          );
          return;
        }

        emitReduxEvent(EVENTS.HANDLE_STABLE_VAULT_DEPOSIT, {
          amount: amount,
          amountOut: convertedAmount,
          referralId,
        });

        // do tx
        setAmount("");
      } else {
        emitReduxEvent(EVENTS.HANDLE_STABLE_VAULT_APPROVE, {
          amount: amount,
          amountOut: convertedAmount,
        });
      }
    }
  }, [
    isConnected,
    isApproved,
    amount,
    openConnectModal,
    convertedAmount,
    referralId,
  ]);

  const handleChange = (value: string) => {
    const val = value.trim().replace(/,/g, "");
    if (!isNaN(+val) || val === "") {
      let normalizedVal = val;
      if (val.startsWith("0") && !val.startsWith("0.")) {
        normalizedVal = val.replace(/^0+/, "");
        if (normalizedVal === "") {
          normalizedVal = "0";
        }
      }
      if (normalizedVal.indexOf(".") === -1) {
        setAmount(normalizedVal);
      } else {
        if (
          normalizedVal.length - (normalizedVal.indexOf(".") + 1) <=
          MAX_FRACTION_DIGITS
        ) {
          setAmount(normalizedVal === "" ? "0." : normalizedVal);
        }
      }
    }
  };

  const handleMaxClick = useCallback(() => {
    if (tokenBalance && tokenBalance > 0)
      setAmount(toFixedWithoutRounding(tokenBalance.toString(), 4));
  }, [tokenBalance]);

  const handleTokenSelect = (key: string) => {
    dispatch(
      updateUserData({
        ["selectedERC20StableVault"]: key,
      })
    );
  };

  return (
    <Flex
      fontSize="14px"
      lineHeight="20px"
      fontWeight="400"
      pt="20px"
      direction={"column"}
    >
      <Flex
        justifyContent={"space-between"}
        mx="16px"
        mb="15px"
        pt={{ base: "10px", md: "20px" }}
        px={{ base: "8px", md: "16px" }}
        pb={{ base: "10px", md: insufficientFund ? "0px" : "20px" }}
        gap={{ base: "20px", md: "12px" }}
        direction={{ base: "column", md: "row" }}
      >
        <Flex
          width={{ base: "100%", md: "38%" }}
          minW={{ base: "100%", md: "251px" }}
          direction={"column"}
        >
          <Box h="12px">
            <Text fontSize="12px" lineHeight="14.4px" fontWeight="400">
              You deposit
            </Text>
          </Box>

          <Flex>
            <StableTicketInput
              inputRightElement={
                <StableTokenSelect
                  onTokenSelect={handleTokenSelect}
                  selected={selectedERC20StableVault}
                  isStableVaultDeposit={true}
                />
              }
              value={amount}
              onChange={(value: string) => handleChange(value)}
              error={error}
            />
          </Flex>

          <Flex mt="8px">
            <StableTicketInputBalance
              label="Available"
              tokenAmount={tokenBalance}
              onClick={handleMaxClick}
              isConnected={isConnected}
            />
          </Flex>
        </Flex>

        <Flex
          direction={"column"}
          width={{ base: "100%", md: "38%" }}
          minW={{ base: "100%", md: "251px" }}
        >
          <Box h="12px">
            <Text fontSize="12px" lineHeight="14.4px" fontWeight="400">
              You get
            </Text>
          </Box>

          <Flex>
            <StableTicketInput
              inputRightElement={
                <StableTokenSelect
                  disabled={true}
                  selected={stableVaultConfig?.xToken?.symbol || "sbUSD"}
                  tokenBgColor="#ECECEC"
                />
              }
              value={
                formatNumber(
                  Number(convertedAmount),
                  false,
                  MAX_FRACTION_DIGITS,
                  true
                ) || "0"
              }
              // onChange={(value: string) => handleChange(value)}
              error={error}
              disabled={true}
            />
          </Flex>
          <Flex mt="8px">
            <StableTicketInputBalance
              label="Balance"
              tokenAmount={xTokenStableVaultBalance}
              token={stableVaultConfig?.xToken?.symbol}
              onClick={() => {}}
              isConnected={isConnected}
              hideMax
            />
          </Flex>
        </Flex>

        <Flex alignItems={"center"} justifyContent={"center"}>
          <Button
            borderRadius="12px"
            bg="textPrimary"
            p="8px 16px"
            h="48px"
            mt={{ base: 0, md: isConnected ? "-10px" : "12px" }}
            width="100%"
            _hover={{
              bg: "textPrimary",
            }}
            onClick={handleClick}
            isDisabled={isConnected && (error !== "" || amount === "")}
          >
            <Text
              fontSize="16px"
              lineHeight="24px"
              fontWeight="600"
              color="backgroundCard"
            >
              {getButtonText()}
            </Text>
          </Button>
        </Flex>
      </Flex>

      {!insufficientFund && error && (
        <Flex
          bgColor="#FF616A"
          p="12px 24px"
          borderRadius={"6px"}
          m={{ base: "0 24px 24px 24px", md: "0 32px 24px 32px" }}
        >
          <Text
            color="white"
            fontSize={"13px"}
            fontWeight={"500"}
            lineHeight={"15.6px"}
          >
            {error}
          </Text>
        </Flex>
      )}

      {insufficientFund && (
        <Flex
          bgColor="#FF616A"
          p="12px 24px"
          borderRadius={"6px"}
          m={{ base: "0 24px 24px 24px", md: "0 32px 24px 32px" }}
        >
          <Text
            color="white"
            fontSize={"13px"}
            fontWeight={"500"}
            lineHeight={"15.6px"}
          >
            Insufficient Balance. Please try again
          </Text>
        </Flex>
      )}

      <Box
        w={"100%"}
        borderTop="1px solid"
        borderColor="gray.200"
        pb={2}
        px="30px"
      >
        <Flex
          gap={{ base: 0, md: "10px" }}
          direction={{ base: "column", md: "row" }}
        >
          <>
            <Flex mt="1rem" gap="4px">
              <Text fontSize={"12px"} fontWeight={"400"} color="#A1B6BA">
                Exchange rate
              </Text>
              {stableAssetExchangeRate.isLoading ? (
                <Skeleton height="20px" w="120px" />
              ) : (
                <>
                  {stableAssetExchangeRate.toERC20 ? (
                    <Text fontSize={"12px"} fontWeight={"600"} color="#3F6870">
                      {`1 ${stableVaultConfig?.xToken?.symbol} = ${
                        Number(stableAssetExchangeRate.toERC20) === 1
                          ? "1"
                          : toFixedWithoutRounding(
                              stableAssetExchangeRate.toERC20.toString(),
                              4
                            )
                      } ${selectedERC20StableVault}`}
                    </Text>
                  ) : null}
                </>
              )}
            </Flex>
          </>

          {/* {depositLimit ? (
            <Flex mt={{ base: "4px", md: "1rem" }} gap="4px">
              <Text fontSize={"12px"} fontWeight={"400"} color="#A1B6BA">
                Deposit limit
              </Text>
              <Text fontSize={"12px"} fontWeight={"600"} color="#3F6870">
                {formatNumber(depositLimit)} {selectedERC20YieldVault}
              </Text>
            </Flex>
          ) : null}
          {minDepositAmount ? (
            <Flex mt={{ base: "4px", md: "1rem" }} gap="4px">
              <Text fontSize={"12px"} fontWeight={"400"} color="#A1B6BA">
                Minimum Deposit
              </Text>
              <Text fontSize={"12px"} fontWeight={"600"} color="#3F6870">
                {minDepositAmount} {selectedERC20YieldVault}
              </Text>
            </Flex>
          ) : null} */}
        </Flex>
      </Box>
    </Flex>
  );
};

export default StableDeposit;
